<template>
  <HrPageLayout
    title="Account Security"
    subtitle="Update your password and manage sign-in protection"
    icon="🔐"
  >
    <div class="cp-page">
      <!-- Account overview -->
      <div class="hr-metrics-grid cp-metrics">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-user"></i></div>
          <p class="hr-metric-label">Signed in as</p>
          <h2 class="hr-metric-value cp-metric-name">{{ userInfo.names || '—' }}</h2>
        </div>
        <div class="hr-metric-card" :class="expiryCardClass">
          <div class="hr-metric-icon"><i class="bx bx-calendar-event"></i></div>
          <p class="hr-metric-label">Password expiry</p>
          <h2 class="hr-metric-value cp-metric-sm">{{ passwordExpiryDisplay }}</h2>
        </div>
        <div class="hr-metric-card" :class="twoFactorEnabled ? 'gradient-2' : 'gradient-4'">
          <div class="hr-metric-icon"><i class="bx bx-shield-quarter"></i></div>
          <p class="hr-metric-label">Two-factor auth</p>
          <h2 class="hr-metric-value cp-metric-sm">{{ twoFactorEnabled ? 'Enabled' : 'Disabled' }}</h2>
        </div>
      </div>

      <div class="cp-grid">
        <!-- Password form -->
        <section class="cp-main">
          <div class="hr-chart-card">
            <div class="hr-chart-header">
              <div>
                <h3 class="hr-chart-title">Change password</h3>
                <p class="cp-chart-sub">Use a strong, unique password you do not use elsewhere</p>
              </div>
            </div>
            <div class="hr-chart-body">
              <form class="cp-form" @submit.prevent="changePassword">
                <div class="cp-field">
                  <label class="hr-form-label" for="currentPassword">Current password <span class="required">*</span></label>
                  <div class="cp-input-wrap" :class="{ 'cp-input-wrap--error': errors.currentPassword }">
                    <i class="bx bx-lock-alt cp-input-icon"></i>
                    <input
                      id="currentPassword"
                      v-model="formData.currentPassword"
                      :type="showCurrentPassword ? 'text' : 'password'"
                      class="cp-input"
                      placeholder="Enter current password"
                      autocomplete="current-password"
                      required
                    />
                    <button type="button" class="cp-toggle" @click="showCurrentPassword = !showCurrentPassword" :aria-label="showCurrentPassword ? 'Hide password' : 'Show password'">
                      <i :class="showCurrentPassword ? 'bx bx-hide' : 'bx bx-show'"></i>
                    </button>
                  </div>
                  <span v-if="errors.currentPassword" class="cp-error">{{ errors.currentPassword }}</span>
                </div>

                <div class="cp-field">
                  <label class="hr-form-label" for="newPassword">New password <span class="required">*</span></label>
                  <div class="cp-input-wrap" :class="{ 'cp-input-wrap--error': errors.newPassword }">
                    <i class="bx bx-lock cp-input-icon"></i>
                    <input
                      id="newPassword"
                      v-model="formData.newPassword"
                      :type="showNewPassword ? 'text' : 'password'"
                      class="cp-input"
                      placeholder="Create a strong password"
                      autocomplete="new-password"
                      required
                      @input="validatePassword"
                    />
                    <button type="button" class="cp-toggle" @click="showNewPassword = !showNewPassword" :aria-label="showNewPassword ? 'Hide password' : 'Show password'">
                      <i :class="showNewPassword ? 'bx bx-hide' : 'bx bx-show'"></i>
                    </button>
                  </div>
                  <span v-if="errors.newPassword" class="cp-error">{{ errors.newPassword }}</span>
                  <div v-if="formError" class="cp-alert cp-alert--error" role="alert">
                    <i class="bx bx-error-circle"></i>
                    <div>
                      <strong>Password update failed</strong>
                      <p>{{ formError }}</p>
                      <ul v-if="formErrorDetails.length" class="cp-alert-list">
                        <li v-for="(detail, idx) in formErrorDetails" :key="idx">{{ detail }}</li>
                      </ul>
                    </div>
                  </div>

                  <div v-if="formData.newPassword" class="cp-strength">
                    <div class="cp-strength__head">
                      <span>Password strength</span>
                      <strong :class="strengthTextClass">{{ passwordStrengthText }}</strong>
                    </div>
                    <div class="cp-strength__track">
                      <div class="cp-strength__fill" :class="strengthBarClass" :style="{ width: passwordStrengthPercentage + '%' }"></div>
                    </div>
                    <div class="cp-requirements">
                      <span v-for="req in requirementItems" :key="req.key" class="cp-req" :class="{ 'cp-req--ok': req.met }">
                        <i :class="req.met ? 'bx bx-check-circle' : 'bx bx-circle'"></i>
                        {{ req.label }}
                      </span>
                    </div>
                  </div>
                </div>

                <div class="cp-field">
                  <label class="hr-form-label" for="confirmPassword">Confirm new password <span class="required">*</span></label>
                  <div class="cp-input-wrap" :class="{ 'cp-input-wrap--error': errors.confirmPassword, 'cp-input-wrap--ok': passwordsMatch }">
                    <i class="bx bx-check-shield cp-input-icon"></i>
                    <input
                      id="confirmPassword"
                      v-model="formData.confirmPassword"
                      :type="showConfirmPassword ? 'text' : 'password'"
                      class="cp-input"
                      placeholder="Re-enter new password"
                      autocomplete="new-password"
                      required
                      @input="validateConfirmPassword"
                    />
                    <button type="button" class="cp-toggle" @click="showConfirmPassword = !showConfirmPassword" :aria-label="showConfirmPassword ? 'Hide password' : 'Show password'">
                      <i :class="showConfirmPassword ? 'bx bx-hide' : 'bx bx-show'"></i>
                    </button>
                  </div>
                  <span v-if="errors.confirmPassword" class="cp-error">{{ errors.confirmPassword }}</span>
                  <span v-else-if="passwordsMatch" class="cp-success"><i class="bx bx-check"></i> Passwords match</span>
                </div>

                <div class="cp-actions">
                  <button type="button" class="hr-btn hr-btn-outline-secondary" @click="resetForm" :disabled="loading">
                    <i class="bx bx-reset"></i> Reset
                  </button>
                  <button type="submit" class="hr-btn hr-btn-primary" :disabled="loading">
                    <span v-if="loading" class="cp-btn-loading"><span class="cp-spinner"></span> Updating…</span>
                    <span v-else><i class="bx bx-lock-open"></i> Update password</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </section>

        <!-- Sidebar -->
        <aside class="cp-aside">
          <div class="hr-chart-card cp-tip-card">
            <div class="hr-chart-body">
              <h4 class="cp-aside-title"><i class="bx bx-info-circle"></i> Password policy</h4>
              <ul class="cp-policy-list">
                <li>Minimum 8 characters</li>
                <li>Uppercase and lowercase letters</li>
                <li>At least one number</li>
                <li>At least one special character (!@#$%^&*…)</li>
                <li>Must meet all requirements above</li>
                <li>Passwords expire every 45 days</li>
              </ul>
            </div>
          </div>

          <div class="hr-chart-card">
            <div class="hr-chart-body">
              <h4 class="cp-aside-title"><i class="bx bx-shield"></i> Security options</h4>
              <div class="cp-security-list">
                <button type="button" class="cp-security-item" @click="manage2FA">
                  <span class="cp-security-item__icon cp-security-item__icon--primary"><i class="bx bx-mobile-alt"></i></span>
                  <span class="cp-security-item__text">
                    <strong>Two-factor authentication</strong>
                    <small>{{ twoFactorEnabled ? 'OTP required at login' : 'Add OTP verification on sign-in' }}</small>
                  </span>
                  <i class="bx bx-chevron-right cp-security-item__arrow"></i>
                </button>
                <button type="button" class="cp-security-item" @click="viewLoginHistory">
                  <span class="cp-security-item__icon cp-security-item__icon--info"><i class="bx bx-history"></i></span>
                  <span class="cp-security-item__text">
                    <strong>Login history</strong>
                    <small>Review recent sign-in activity</small>
                  </span>
                  <i class="bx bx-chevron-right cp-security-item__arrow"></i>
                </button>
              </div>
            </div>
          </div>

          <div class="cp-callout">
            <i class="bx bx-bulb"></i>
            <p>Never reuse passwords across sites. Use a passphrase you can remember, or a password manager.</p>
          </div>
        </aside>
      </div>
    </div>

    <!-- Login History Modal -->
    <div v-if="showLoginHistoryModal" class="cp-modal-overlay" @click.self="closeLoginHistoryModal">
      <div class="cp-modal cp-modal-lg" role="dialog" aria-labelledby="loginHistoryTitle">
        <div class="cp-modal-header">
          <div>
            <h5 id="loginHistoryTitle" class="cp-modal-title"><i class="bx bx-history"></i> Login history</h5>
            <p class="cp-modal-sub">Recent sign-in and security events on your account</p>
          </div>
          <button type="button" class="cp-modal-close" @click="closeLoginHistoryModal" aria-label="Close"><i class="bx bx-x"></i></button>
        </div>
        <div class="cp-modal-body">
          <div v-if="historyLoading" class="cp-modal-loading">
            <span class="cp-spinner"></span>
            <span>Loading activity…</span>
          </div>
          <div v-else-if="loginHistory.length === 0" class="cp-empty">
            <i class="bx bx-info-circle"></i>
            <p>No login activity recorded yet.</p>
          </div>
          <div v-else class="hr-table-wrapper">
            <table class="hr-table">
              <thead>
                <tr>
                  <th>Date &amp; time</th>
                  <th>Event</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(entry, idx) in loginHistory" :key="entry.id || idx">
                  <td class="cp-nowrap">{{ formatActivityDate(entry.activityDate || entry.actionDate || entry.date) }}</td>
                  <td><span class="cp-badge" :class="activityBadgeClass(entry)">{{ activityLabel(entry) }}</span></td>
                  <td class="cp-muted">{{ entry.description || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="cp-modal-footer">
          <button type="button" class="hr-btn hr-btn-outline-secondary" @click="closeLoginHistoryModal">Close</button>
        </div>
      </div>
    </div>

    <!-- Two-Factor Authentication Modal -->
    <div v-if="show2FAModal" class="cp-modal-overlay" @click.self="close2FAModal">
      <div class="cp-modal" role="dialog" aria-labelledby="twoFactorTitle">
        <div class="cp-modal-header">
          <div>
            <h5 id="twoFactorTitle" class="cp-modal-title"><i class="bx bx-mobile-alt"></i> Two-factor authentication</h5>
            <p class="cp-modal-sub">Require an OTP on your phone when signing in</p>
          </div>
          <button type="button" class="cp-modal-close" @click="close2FAModal" aria-label="Close"><i class="bx bx-x"></i></button>
        </div>
        <div class="cp-modal-body">
          <div class="cp-2fa-panel">
            <div class="cp-2fa-panel__info">
              <strong>OTP at login</strong>
              <p>
                Status:
                <span :class="twoFactorEnabled ? 'cp-text-success' : 'cp-text-muted'">
                  {{ twoFactorEnabled ? 'Enabled' : 'Disabled' }}
                </span>
              </p>
              <p v-if="securityPhone" class="cp-muted">Phone: {{ securityPhone }}</p>
              <p v-else class="cp-text-warn">No phone number on file — required to enable 2FA.</p>
            </div>
            <label class="cp-switch">
              <input
                type="checkbox"
                :checked="twoFactorEnabled"
                :disabled="twoFactorLoading || (!twoFactorEnabled && !securityPhone)"
                @change="onTwoFactorToggle"
              />
              <span class="cp-switch__slider"></span>
            </label>
          </div>
          <p class="cp-modal-note">
            <i class="bx bx-info-circle"></i>
            Changes apply on your next login. Keep your phone number updated in your employee profile.
          </p>
        </div>
        <div class="cp-modal-footer">
          <button type="button" class="hr-btn hr-btn-outline-secondary" @click="close2FAModal" :disabled="twoFactorLoading">Close</button>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, toast } from "@/api";

export default {
  name: "ChangePassword",
  components: {
    HrPageLayout
  },
  data() {
    return {
      loading: false,
      userInfo: {
        names: '',
        idNo: '',
        requireOTP: 'false',
        passwordExpiry: null
      },
      formData: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      errors: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      formError: '',
      formErrorDetails: [],
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
      passwordChecks: {
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false
      },
      showLoginHistoryModal: false,
      show2FAModal: false,
      historyLoading: false,
      twoFactorLoading: false,
      loginHistory: [],
      securityPhone: ''
    };
  },
  computed: {
    twoFactorEnabled() {
      const val = String(this.userInfo.requireOTP || '').toLowerCase();
      return val === 'true' || val === '1' || val === 'yes';
    },
    passwordsMatch() {
      return Boolean(
        this.formData.confirmPassword &&
        this.formData.newPassword &&
        this.formData.newPassword === this.formData.confirmPassword
      );
    },
    requirementItems() {
      return [
        { key: 'length', label: 'At least 8 characters', met: this.passwordChecks.length },
        { key: 'uppercase', label: 'At least one uppercase letter (A–Z)', met: this.passwordChecks.uppercase },
        { key: 'lowercase', label: 'At least one lowercase letter (a–z)', met: this.passwordChecks.lowercase },
        { key: 'number', label: 'At least one number (0–9)', met: this.passwordChecks.number },
        { key: 'special', label: 'At least one special character (!@#$%^&*…)', met: this.passwordChecks.special }
      ];
    },
    unmetRequirements() {
      return this.requirementItems.filter((r) => !r.met).map((r) => r.label);
    },
    allRequirementsMet() {
      return this.unmetRequirements.length === 0;
    },
    isFormValid() {
      return (
        this.formData.currentPassword &&
        this.formData.newPassword &&
        this.formData.confirmPassword &&
        this.formData.newPassword === this.formData.confirmPassword &&
        this.allRequirementsMet &&
        this.formData.currentPassword !== this.formData.newPassword &&
        !this.errors.currentPassword &&
        !this.errors.newPassword &&
        !this.errors.confirmPassword
      );
    },
    passwordStrength() {
      let strength = 0;
      if (this.passwordChecks.length) strength++;
      if (this.passwordChecks.uppercase) strength++;
      if (this.passwordChecks.lowercase) strength++;
      if (this.passwordChecks.number) strength++;
      if (this.passwordChecks.special) strength++;
      return strength;
    },
    passwordStrengthPercentage() {
      return (this.passwordStrength / 5) * 100;
    },
    passwordStrengthText() {
      if (this.passwordStrength === 0) return 'Very weak';
      if (this.passwordStrength === 1) return 'Weak';
      if (this.passwordStrength === 2) return 'Fair';
      if (this.passwordStrength === 3) return 'Good';
      if (this.passwordStrength === 4) return 'Strong';
      return 'Very strong';
    },
    strengthBarClass() {
      if (this.passwordStrength <= 1) return 'cp-strength--weak';
      if (this.passwordStrength === 2) return 'cp-strength--fair';
      if (this.passwordStrength === 3) return 'cp-strength--good';
      return 'cp-strength--strong';
    },
    strengthTextClass() {
      return this.strengthBarClass.replace('cp-strength', 'cp-strength-text');
    },
    passwordExpiryDays() {
      if (!this.userInfo.passwordExpiry) return null;
      const expiryDate = new Date(this.userInfo.passwordExpiry);
      const today = new Date();
      return Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
    },
    passwordExpiryDisplay() {
      if (!this.userInfo.passwordExpiry) return 'Not set';
      const days = this.passwordExpiryDays;
      if (days < 0) return 'Expired';
      if (days === 0) return 'Expires today';
      if (days <= 7) return `${days} days left`;
      return new Date(this.userInfo.passwordExpiry).toLocaleDateString();
    },
    expiryCardClass() {
      const days = this.passwordExpiryDays;
      if (days === null) return 'gradient-4';
      if (days < 0) return 'gradient-3';
      if (days <= 7) return 'gradient-4';
      return 'gradient-2';
    }
  },
  mounted() {
    this.loadUserInfo();
    this.refreshSecuritySettings();
  },
  methods: {
    loadUserInfo() {
      try {
        const userInfoStr = sessionStorage.getItem('userInfo');
        if (userInfoStr) {
          this.userInfo = { ...this.userInfo, ...JSON.parse(userInfoStr) };
        }
      } catch (error) {
        console.error('Error loading user info:', error);
        toast.error('Failed to load user information');
      }
    },

    async refreshSecuritySettings() {
      try {
        const data = new FormData();
        data.append('function', 'getMySecuritySettings');
        const res = await execute(data, authUrl);
        if (res.data.success && res.data.data) {
          this.userInfo.requireOTP = res.data.data.requireOTP;
          this.securityPhone = res.data.data.phoneNumber || '';
          sessionStorage.setItem('userInfo', JSON.stringify({
            ...JSON.parse(sessionStorage.getItem('userInfo') || '{}'),
            requireOTP: res.data.data.requireOTP
          }));
        }
      } catch (error) {
        console.error('Error loading security settings:', error);
      }
    },

    validatePassword() {
      const password = this.formData.newPassword || '';

      this.passwordChecks.length = password.length >= 8;
      this.passwordChecks.uppercase = /[A-Z]/.test(password);
      this.passwordChecks.lowercase = /[a-z]/.test(password);
      this.passwordChecks.number = /[0-9]/.test(password);
      // Keep in sync with backend changePassword special-char rule
      this.passwordChecks.special = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>\/?`~]/.test(password);

      this.formError = '';
      this.formErrorDetails = [];

      if (!password) {
        this.errors.newPassword = '';
      } else if (!this.allRequirementsMet) {
        const missing = this.unmetRequirements;
        this.errors.newPassword =
          missing.length === 1
            ? `Missing: ${missing[0]}`
            : `Password does not meet all requirements (${missing.length} missing)`;
      } else if (this.formData.currentPassword && password === this.formData.currentPassword) {
        this.errors.newPassword = 'New password must be different from current password';
      } else {
        this.errors.newPassword = '';
      }

      if (this.formData.confirmPassword) {
        this.validateConfirmPassword();
      }
    },

    validateConfirmPassword() {
      if (this.formData.newPassword !== this.formData.confirmPassword) {
        this.errors.confirmPassword = 'Passwords do not match';
      } else {
        this.errors.confirmPassword = '';
      }
    },

    resetForm() {
      this.formData = {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      };
      this.errors = {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      };
      this.formError = '';
      this.formErrorDetails = [];
      this.showCurrentPassword = false;
      this.showNewPassword = false;
      this.showConfirmPassword = false;
      this.passwordChecks = {
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false
      };
    },

    setPasswordFailure(message, details = []) {
      const msg = String(message || 'Failed to change password');
      this.formError = msg;
      this.formErrorDetails = Array.isArray(details) ? details.filter(Boolean) : [];
      const lower = msg.toLowerCase();
      if (lower.includes('current password')) {
        this.errors.currentPassword = msg;
      } else if (
        lower.includes('special character') ||
        lower.includes('uppercase') ||
        lower.includes('lowercase') ||
        lower.includes('number') ||
        lower.includes('at least 8') ||
        lower.includes('requirements') ||
        lower.includes('different from current')
      ) {
        this.errors.newPassword = msg;
      }
      toast.error(msg);
    },

    async changePassword() {
      this.formError = '';
      this.formErrorDetails = [];
      this.validatePassword();
      this.validateConfirmPassword();

      if (!this.formData.currentPassword) {
        this.errors.currentPassword = 'Current password is required';
      }

      if (!this.formData.newPassword) {
        this.errors.newPassword = 'New password is required';
      } else if (!this.allRequirementsMet) {
        this.setPasswordFailure(
          'Password does not meet all requirements',
          this.unmetRequirements
        );
        return;
      }

      if (!this.formData.confirmPassword) {
        this.errors.confirmPassword = 'Please confirm your new password';
      } else if (this.formData.newPassword !== this.formData.confirmPassword) {
        this.errors.confirmPassword = 'Passwords do not match';
        toast.error('Passwords do not match');
        return;
      }

      if (this.formData.currentPassword === this.formData.newPassword) {
        this.setPasswordFailure('New password must be different from current password');
        return;
      }

      if (!this.isFormValid) {
        this.setPasswordFailure(
          'Please fix the highlighted errors before updating your password',
          this.unmetRequirements
        );
        return;
      }

      this.loading = true;

      const data = new FormData();
      data.append("function", "changePassword");
      data.append("currentPassword", btoa(this.formData.currentPassword));
      data.append("newPassword", btoa(this.formData.newPassword));
      data.append("idNo", this.userInfo.idNo);

      try {
        const res = await execute(data, authUrl);
        this.loading = false;

        if (res.data.success) {
          this.formError = '';
          this.formErrorDetails = [];
          toast.success('Changed successfully');
          this.resetForm();
          setTimeout(() => {
            toast.info('Please login with your new password');
            sessionStorage.clear();
            this.$router.push('/');
          }, 2000);
        } else {
          const details = res.data.data?.requirements || res.data.data?.errors || [];
          this.setPasswordFailure(res.data.message || 'Failed to change password', details);
        }
      } catch (error) {
        this.loading = false;
        console.error('Error changing password:', error);
        const details = error?.response?.data?.data?.requirements
          || error?.response?.data?.data?.errors
          || [];
        this.setPasswordFailure(
          error.message || 'An error occurred while changing password',
          details
        );
      }
    },

    async manage2FA() {
      await this.refreshSecuritySettings();
      this.show2FAModal = true;
    },

    close2FAModal() {
      if (!this.twoFactorLoading) {
        this.show2FAModal = false;
      }
    },

    async onTwoFactorToggle(event) {
      const enable = event.target.checked;
      if (enable && !this.securityPhone) {
        event.target.checked = false;
        toast.error('Add a phone number to your profile before enabling 2FA');
        return;
      }

      this.twoFactorLoading = true;
      const data = new FormData();
      data.append('function', 'updateMyRequireOTP');
      data.append('requireOTP', enable ? 'true' : 'false');

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          const requireOTP = res.data.data?.requireOTP || (enable ? 'true' : 'false');
          this.userInfo.requireOTP = requireOTP;
          const stored = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
          sessionStorage.setItem('userInfo', JSON.stringify({ ...stored, requireOTP }));
          toast.success(res.data.message || (enable ? '2FA enabled' : '2FA disabled'));
        } else {
          event.target.checked = !enable;
          toast.error(res.data.message || 'Failed to update 2FA setting');
        }
      } catch (error) {
        event.target.checked = !enable;
        console.error('2FA update error:', error);
        toast.error('Failed to update two-factor authentication');
      } finally {
        this.twoFactorLoading = false;
      }
    },

    async viewLoginHistory() {
      this.showLoginHistoryModal = true;
      this.historyLoading = true;
      this.loginHistory = [];

      const data = new FormData();
      data.append('function', 'getUserLoginHistory');
      data.append('limit', '25');

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.loginHistory = res.data.data?.history || [];
        } else {
          toast.error(res.data.message || 'Failed to load login history');
        }
      } catch (error) {
        console.error('Login history error:', error);
        toast.error('Failed to load login history');
      } finally {
        this.historyLoading = false;
      }
    },

    closeLoginHistoryModal() {
      this.showLoginHistoryModal = false;
    },

    formatActivityDate(value) {
      if (!value) return '—';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;
      return d.toLocaleString();
    },

    activityLabel(entry) {
      const type = (entry.actionType || '').toUpperCase();
      if (type === 'LOGIN') return 'Login';
      if (type === 'LOGOUT') return 'Logout';
      if (type === 'FAILED_LOGIN') return 'Failed login';
      if (type === 'PASSWORD_CHANGE') return 'Password change';
      if (type === 'SECURITY_SETTING') return 'Security';
      const desc = (entry.description || '').toLowerCase();
      if (desc.includes('invalid password')) return 'Failed login';
      if (desc.includes('logged in') || desc.includes('log in')) return 'Login';
      if (desc.includes('password changed')) return 'Password change';
      return 'Activity';
    },

    activityBadgeClass(entry) {
      const label = this.activityLabel(entry);
      if (label === 'Failed login') return 'cp-badge--danger';
      if (label === 'Login') return 'cp-badge--success';
      if (label === 'Password change' || label === 'Security') return 'cp-badge--info';
      return 'cp-badge--muted';
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.cp-page {
  max-width: 1200px;
}

.cp-chart-sub {
  margin: 4px 0 0;
  font-size: 13px;
  color: #64748b;
}

.cp-metrics .hr-metric-value {
  font-size: 1.35rem;
  line-height: 1.2;
}

.cp-metric-name {
  font-size: 1.1rem !important;
  word-break: break-word;
}

.cp-metric-sm {
  font-size: 1.25rem !important;
}

.cp-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(280px, 0.9fr);
  gap: 24px;
  align-items: start;
}

.cp-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.cp-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.cp-input-wrap {
  display: flex;
  align-items: center;
  background: #fff;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 0 12px 0 14px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.cp-input-wrap:focus-within {
  border-color: #4f46e5;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.12);
}

.cp-input-wrap--error {
  border-color: #ef4444;
}

.cp-input-wrap--error:focus-within {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.12);
}

.cp-input-wrap--ok {
  border-color: #10b981;
}

.cp-input-icon {
  color: #94a3b8;
  font-size: 1.15rem;
  margin-right: 10px;
  flex-shrink: 0;
}

.cp-input {
  flex: 1;
  border: 0;
  background: transparent;
  padding: 12px 0;
  font-size: 14px;
  color: #1e293b;
  min-width: 0;
}

.cp-input:focus {
  outline: none;
}

.cp-toggle {
  border: 0;
  background: transparent;
  color: #64748b;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  flex-shrink: 0;
}

.cp-toggle:hover {
  background: #f1f5f9;
  color: #4f46e5;
}

.cp-error {
  font-size: 12px;
  color: #ef4444;
}

.cp-alert {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.45;
}

.cp-alert--error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}

.cp-alert--error > i {
  font-size: 20px;
  color: #ef4444;
  flex-shrink: 0;
  margin-top: 1px;
}

.cp-alert strong {
  display: block;
  margin-bottom: 4px;
}

.cp-alert p {
  margin: 0;
}

.cp-alert-list {
  margin: 8px 0 0;
  padding-left: 18px;
}

.cp-alert-list li {
  margin-bottom: 2px;
}

.cp-success {
  font-size: 12px;
  color: #10b981;
  display: flex;
  align-items: center;
  gap: 4px;
}

.cp-strength {
  margin-top: 12px;
  padding: 14px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.cp-strength__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 8px;
}

.cp-strength__track {
  height: 6px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
}

.cp-strength__fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.3s ease, background 0.3s ease;
}

.cp-strength--weak,
.cp-strength-text--weak { background: #ef4444; color: #ef4444; }
.cp-strength--fair,
.cp-strength-text--fair { background: #f59e0b; color: #d97706; }
.cp-strength--good,
.cp-strength-text--good { background: #3b82f6; color: #2563eb; }
.cp-strength--strong,
.cp-strength-text--strong { background: #10b981; color: #059669; }

.cp-requirements {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.cp-req {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #e2e8f0;
  color: #94a3b8;
}

.cp-req--ok {
  background: #ecfdf5;
  border-color: #a7f3d0;
  color: #059669;
}

.cp-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;
  padding-top: 8px;
}

.cp-btn-loading {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.cp-aside {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.cp-aside-title {
  margin: 0 0 12px;
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 8px;
}

.cp-aside-title i {
  color: #4f46e5;
}

.cp-policy-list {
  margin: 0;
  padding-left: 18px;
  color: #64748b;
  font-size: 14px;
  line-height: 1.7;
}

.cp-security-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.cp-security-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.15s;
}

.cp-security-item:hover {
  border-color: #c7d2fe;
  box-shadow: 0 4px 14px rgba(79, 70, 229, 0.08);
  transform: translateY(-1px);
}

.cp-security-item__icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.cp-security-item__icon--primary {
  background: #eef2ff;
  color: #4f46e5;
}

.cp-security-item__icon--info {
  background: #e0f2fe;
  color: #0284c7;
}

.cp-security-item__text {
  flex: 1;
  min-width: 0;
}

.cp-security-item__text strong {
  display: block;
  font-size: 14px;
  color: #1e293b;
}

.cp-security-item__text small {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.cp-security-item__arrow {
  color: #cbd5e1;
  font-size: 1.25rem;
}

.cp-callout {
  display: flex;
  gap: 10px;
  padding: 14px 16px;
  background: linear-gradient(135deg, #fef3c7 0%, #fffbeb 100%);
  border: 1px solid #fde68a;
  border-radius: 12px;
  font-size: 13px;
  color: #92400e;
  line-height: 1.5;
}

.cp-callout i {
  font-size: 1.25rem;
  flex-shrink: 0;
  margin-top: 2px;
}

/* Modals */
.cp-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}

.cp-modal {
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.18);
}

.cp-modal-lg {
  max-width: 760px;
}

.cp-modal-header {
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.cp-modal-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 8px;
}

.cp-modal-sub {
  margin: 4px 0 0;
  font-size: 13px;
  color: #64748b;
}

.cp-modal-close {
  border: 0;
  background: #f1f5f9;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.cp-modal-close:hover {
  background: #e2e8f0;
  color: #1e293b;
}

.cp-modal-body {
  padding: 1.25rem 1.5rem;
  overflow-y: auto;
}

.cp-modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
}

.cp-modal-loading,
.cp-empty {
  text-align: center;
  padding: 2.5rem 1rem;
  color: #64748b;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.cp-empty i {
  font-size: 2rem;
  color: #cbd5e1;
}

.cp-2fa-panel {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
  margin-bottom: 16px;
}

.cp-2fa-panel__info strong {
  display: block;
  color: #1e293b;
  margin-bottom: 4px;
}

.cp-2fa-panel__info p {
  margin: 0;
  font-size: 13px;
}

.cp-switch {
  position: relative;
  display: inline-block;
  width: 48px;
  height: 28px;
  flex-shrink: 0;
}

.cp-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.cp-switch__slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #cbd5e1;
  border-radius: 999px;
  transition: 0.25s;
}

.cp-switch__slider::before {
  content: "";
  position: absolute;
  height: 22px;
  width: 22px;
  left: 3px;
  bottom: 3px;
  background: #fff;
  border-radius: 50%;
  transition: 0.25s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.cp-switch input:checked + .cp-switch__slider {
  background: #4f46e5;
}

.cp-switch input:checked + .cp-switch__slider::before {
  transform: translateX(20px);
}

.cp-switch input:disabled + .cp-switch__slider {
  opacity: 0.5;
  cursor: not-allowed;
}

.cp-modal-note {
  margin: 0;
  font-size: 13px;
  color: #64748b;
  display: flex;
  gap: 6px;
  align-items: flex-start;
  padding: 12px;
  background: #eff6ff;
  border-radius: 10px;
}

.cp-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
}

.cp-badge--success { background: #d1fae5; color: #047857; }
.cp-badge--danger { background: #fee2e2; color: #b91c1c; }
.cp-badge--info { background: #dbeafe; color: #1d4ed8; }
.cp-badge--muted { background: #f1f5f9; color: #475569; }

.cp-muted { color: #64748b; font-size: 13px; }
.cp-nowrap { white-space: nowrap; }
.cp-text-success { color: #059669; font-weight: 600; }
.cp-text-muted { color: #64748b; }
.cp-text-warn { color: #d97706; font-size: 12px; }

.cp-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid #e2e8f0;
  border-top-color: #4f46e5;
  border-radius: 50%;
  animation: cp-spin 0.7s linear infinite;
}

@keyframes cp-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 992px) {
  .cp-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 576px) {
  .cp-actions {
    flex-direction: column-reverse;
  }

  .cp-actions .hr-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
